import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, Crown, Sparkles, Loader2, Tag } from "lucide-react";

const SYMBOL = { GBP: "£", USD: "$", EUR: "€" };
const fmtPrice = (p) => p ? `${SYMBOL[p.currency] || p.currency + " "}${Number.isInteger(p.amount) ? p.amount : p.amount.toFixed(2)}` : null;

const PLANS = [
  {
    name: "Free Trial", price: "£0", period: "7 days", cta: "Current plan", highlight: false, lookup: null,
    features: ["3 roleplays per day", "Basic coaching", "Beginner course", "Daily challenge"],
  },
  {
    name: "Pro", price: "£24", period: "/month", cta: "Start Pro — 7 days free", highlight: true, lookup: "pro_monthly",
    features: ["Unlimited roleplays", "Full AI coaching & memory", "All courses", "Voice mode", "Full analytics", "Leaderboards & Battles"],
  },
  {
    name: "Elite Annual", price: "£199", period: "/year", cta: "Go Elite — 7 days free", highlight: false, lookup: "elite_yearly",
    features: ["Everything in Pro", "Save over 30% vs monthly", "Priority AI responses", "Tournaments & teams", "Early access to new features"],
  },
];

export default function Pricing() {
  const { user } = useAuth();
  const [discount, setDiscount] = useState("");
  const [loading, setLoading] = useState(null);
  const [live, setLive] = useState({});
  useEffect(() => { api.get("/plans").then((r) => setLive(r.data || {})).catch((err) => console.warn("[Pricing] request failed", err)); }, []);
  const plans = PLANS.map((p) => (p.lookup && live[p.lookup] ? { ...p, price: fmtPrice(live[p.lookup]) || p.price } : p));

  const checkout = async (plan) => {
    if (!plan.lookup) return;
    setLoading(plan.lookup);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: plan.lookup,
        origin_url: window.location.origin,
        discount_code: discount.trim() || undefined,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start checkout");
      setLoading(null);
    }
  };

  return (
    <div className="space-y-8">
      <div className="text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-600 text-xs font-mono uppercase tracking-widest">
          <Crown className="w-3.5 h-3.5" /> Membership
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-4">Train like the top 1%</h1>
        <p className="text-slate-500 mt-2 max-w-lg mx-auto">Unlimited practice, deeper coaching, and everything you need to become elite. Every paid plan starts with a 7-day free trial.</p>
        {user?.plan && user.plan !== "Free" && (
          <div className="mt-3 inline-flex items-center gap-2 text-emerald-600 text-sm font-semibold"><Check className="w-4 h-4" /> You're on {user.plan}</div>
        )}
      </div>

      <div className="max-w-md mx-auto card-surface rounded-2xl p-4 flex items-center gap-2" data-testid="discount-box">
        <Tag className="w-4 h-4 text-amber-600" />
        <Input value={discount} onChange={(e) => setDiscount(e.target.value.toUpperCase())} placeholder="Have a discount code? (e.g. LINDEN20)"
          className="bg-slate-50 border-slate-200 text-slate-900 uppercase" data-testid="discount-input" />
      </div>

      <div className="grid md:grid-cols-3 gap-4 max-w-5xl mx-auto">
        {plans.map((p, i) => (
          <motion.div key={p.name} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
            className={`rounded-2xl p-6 relative ${p.highlight ? "bg-gradient-to-b from-emerald-500/15 to-[#FFFFFF] border border-emerald-500/40 glow-emerald" : "card-surface"}`}
            data-testid={`plan-${p.name.toLowerCase().replace(/\s/g, "-")}`}>
            {p.highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-emerald-500 text-black text-xs font-bold flex items-center gap-1"><Sparkles className="w-3 h-3" /> Most popular</span>}
            <h3 className="font-display font-bold text-slate-900 text-lg">{p.name}</h3>
            <div className="mt-3 flex items-end gap-1">
              <span className="text-4xl font-extrabold text-slate-900 font-display">{p.price}</span>
              <span className="text-sm text-slate-500 mb-1">{p.period}</span>
            </div>
            <ul className="mt-6 space-y-3">
              {p.features.map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-slate-600">
                  <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <Button onClick={() => p.lookup ? checkout(p) : null} disabled={!p.lookup || loading === p.lookup}
              className={`w-full mt-6 h-11 font-semibold ${p.highlight ? "bg-emerald-500 hover:bg-emerald-600 text-black" : "bg-slate-100 hover:bg-slate-100 text-slate-900 border border-slate-200"}`}
              data-testid={`plan-cta-${i}`}>
              {p.lookup && loading === p.lookup ? <Loader2 className="w-4 h-4 animate-spin" /> : p.cta}
            </Button>
          </motion.div>
        ))}
      </div>
      <p className="text-center text-xs text-slate-600">Secure checkout by Stripe · Test card 4242 4242 4242 4242 · Cancel anytime</p>
    </div>
  );
}
